import React, {
  useEffect,
  useCallback,
  memo,
}                          from 'react';
import { useSelector }     from 'react-redux';
import {
  withRouter,
  RouteComponentProps,
}                          from 'react-router-dom';
import styled              from 'styled-components';
import * as Yup            from 'yup';
import {
  ILoginInitialValues,
  IForgotPasswordInitialValues,
  IResetPasswordInitialValues,
}                          from '@typings/auth';
import { EAppPage }        from '@enums/common';
import { EAuthPage }       from '@enums/auth';
import {
  useValidateRecoverId,
  useLogin,
  useSendRecoveryEmail,
  useResetPassword,
}                          from '@modules/auth/queries/authQueries';
import { getIsAuthorized } from '@modules/auth/authReducer';
import { Login }           from '@modules/auth/components/Login';
import { ForgotPassword }  from '@modules/auth/components/ForgotPassword';
import { ResetPassword }   from '@modules/auth/components/ResetPassword';
import { Header }          from '@components/Header/Header';

const Wrapper = styled.div`
  display          : flex;
  height           : 100%;
  justify-content  : center;
  padding-top      : 200px;
  background-color : ${({ theme: { backgroundColor } }): string => backgroundColor.white};

  span {
    font-size: 16px;
  }
`;

const checkCharacterRegEx = new RegExp('(?=.*[a-zA-Z])');
const checkNumericRegEx   = new RegExp('(?=.*[0-9])');

export const validationSchema = {
  [EAuthPage.LogIn]: Yup.object().shape({
    username : Yup.string().email().required(),
    password : Yup.string().min(8).max(12).required(),
  }),
  [EAuthPage.ForgotPass]: Yup.object().shape({
    email: Yup.string().email().required(),
  }),
  [EAuthPage.ResetPass]: Yup.object().shape({
    newPassword: Yup.string()
      .min(8)
      .max(32)
      .matches(checkCharacterRegEx)
      .matches(checkNumericRegEx)
      .required(),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')])
      .required(),
  }),
};

interface IInitialValues {
  [EAuthPage.LogIn]          : ILoginInitialValues;
  [EAuthPage.ForgotPass] : IForgotPasswordInitialValues;
  [EAuthPage.ResetPass]  : IResetPasswordInitialValues;
}

export const initialValues: IInitialValues = {
  [EAuthPage.LogIn]: {
    username : '',
    password : '',
  },
  [EAuthPage.ForgotPass]: {
    email: '',
  },
  [EAuthPage.ResetPass]: {
    newPassword     : '',
    confirmPassword : '',
  },
};

interface IParams {
  page       : EAuthPage;
  recoverId? : string;
}

interface ILocationState {
  from?: string;
}

const AuthPageComponent: React.FC<RouteComponentProps<IParams, any, ILocationState>> = ({
  match   : {
    params: {
      page = EAuthPage.LogIn,
      recoverId,
    },
  },
  history,
  location,
}) => {
  const { push, replace } = history;

  const isAuthorized = useSelector(getIsAuthorized);

  // #region API INTEGRATION
  const { mutateAsync: validateRecoverId } = useValidateRecoverId();

  const { mutateAsync: login, isLoading: isLogging } = useLogin();

  const {
    mutateAsync : sendRecoveryEmail,
    isLoading   : isEmailSending,
    isSuccess,
  } = useSendRecoveryEmail();

  const { mutateAsync: resetPassword, isLoading: isPasswordResetting } = useResetPassword();
  // #endregion

  useEffect(
    (): void => {
      if (isAuthorized) {
        const redirectTo = location?.state?.from || EAppPage.Dashboard;

        replace(redirectTo);
      } else if (page === EAuthPage.ResetPass && recoverId) {
        validateRecoverId(recoverId);
      }
    },
    [isAuthorized, replace, location, page, validateRecoverId, recoverId],
  );

  const handleForgotPassword = useCallback(
    (): void => {
      push(`/${EAuthPage.ForgotPass}`);
    },
    [push],
  );

  const handleSendRecoveryMail = useCallback(
    async ({ email }: IForgotPasswordInitialValues): Promise<void> => {
      await sendRecoveryEmail(email);
    },
    [sendRecoveryEmail],
  );

  const handleResetPassword = useCallback(
    async ({ newPassword }: IResetPasswordInitialValues): Promise<void> => {
      if (!recoverId) {
        return;
      }

      await resetPassword({ recoverId, newPassword });
    },
    [recoverId, resetPassword],
  );

  return (
    <>
      <Header
        history      = {history}
        isAuthorized = {isAuthorized}
        handleLogout = {(): null => null}
        handleGoHome = {(): void => push('/')}
      />

      <Wrapper>
        {page === EAuthPage.LogIn && (
          <Login
            isLogging            = {isLogging}
            initialValues        = {initialValues[EAuthPage.LogIn]}
            validationSchema     = {validationSchema[EAuthPage.LogIn]}
            isRecoveryEmailSent  = {isSuccess}
            handleLogin          = {login}
            handleForgotPassword = {handleForgotPassword}
          />
        )}

        {page === EAuthPage.ForgotPass && (
          <ForgotPassword
            isEmailSending         = {isEmailSending}
            initialValues          = {initialValues[EAuthPage.ForgotPass]}
            validationSchema       = {validationSchema[EAuthPage.ForgotPass]}
            handleSendRecoveryMail = {handleSendRecoveryMail}
          />
        )}

        {page === EAuthPage.ResetPass && (
          <ResetPassword
            isPasswordResetting = {isPasswordResetting}
            initialValues       = {initialValues[EAuthPage.ResetPass]}
            validationSchema    = {validationSchema[EAuthPage.ResetPass]}
            handleResetPassword = {handleResetPassword}
          />
        )}
      </Wrapper>
    </>
  );
};

export const AuthPage = memo(withRouter(AuthPageComponent));
